import React from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { calendar, calendarMonths } from "../data/mock";
import { useSpecies, useSpeciesList } from "../i18n/content";
import { Eyebrow, Reveal, CTASection } from "../components/common";
import { Button } from "../components/ui/button";
import { useDeposit } from "../context/DepositContext";
import { ArrowLeft, Anchor, Ruler, CalendarDays, Target, MapPin } from "lucide-react";
import Seo from "../components/Seo";

const cell = { P: "bg-[#f5a524] text-[#0a1f33]", G: "bg-[#0e9488] text-white", O: "bg-white/10 text-slate-500" };

const SpeciesDetail = () => {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { openDeposit } = useDeposit();
  const s = useSpecies(slug);
  const all = useSpeciesList();
  if (!s) return (<div className="container-x py-40 text-center"><h1 className="font-display text-4xl text-white">Species not found</h1><Link to="/species" className="text-[#f5a524] mt-4 inline-block">{t("species.backToSpecies")}</Link></div>);
  const row = calendar.find((r) => r.name === s.name || (s.name.includes(r.name || "") && r.name));
  const related = all.filter((x) => x.zone === s.zone && x.slug !== s.slug).slice(0, 3);
  const zoneLabel = t(`plan.${s.zone}`);
  return (
    <>
      <Seo
        title={`${s.name} Fishing in San Juan del Sur, Nicaragua | Hog Tide`}
        description={`Catch ${s.name} (${s.latin}) in San Juan del Sur, Nicaragua. Best season ${s.peak} · ${s.method}. Book a private Hog Tide ${s.zone} fishing charter.`}
        path={`/species/${s.slug}`}
        image={s.image}
      />
      <section className="relative min-h-[52vh] flex items-end overflow-hidden">
        <img src={s.image} alt={s.name} className="absolute inset-0 h-full w-full object-cover object-center animate-kenburns" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f33] via-[#0a1f33]/60 to-transparent" />
        <div className="container-x relative z-10 pb-10 pt-28">
          <Link to={`/${s.zone}-species`} className="inline-flex items-center gap-1 text-slate-300 hover:text-[#f5a524] text-sm mb-3"><ArrowLeft className="h-4 w-4" /> {zoneLabel}</Link>
          <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold uppercase mb-3 ${s.zone === "offshore" ? "bg-[#1c6ba3]" : "bg-[#0e9488]"} text-white`}>{zoneLabel}</span>
          <h1 className="font-display text-5xl md:text-6xl text-white text-shadow-hero">{s.name}</h1>
          <p className="italic text-slate-300 mt-1">{s.latin}</p>
        </div>
      </section>

      <section className="py-14">
        <div className="container-x grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <Reveal>
              <Eyebrow>{t("speciesList.aboutSpecies")}</Eyebrow>
              <p className="text-lg text-slate-200 leading-relaxed">{s.blurb}</p>
              {row && (
                <div className="mt-8">
                  <h3 className="font-heading text-xl text-white mb-3 flex items-center gap-2"><CalendarDays className="h-5 w-5 text-[#f5a524]" /> {t("speciesList.seasonByMonth")}</h3>
                  <div className="grid grid-cols-12 gap-1">
                    {calendarMonths.map((m, i) => (
                      <div key={m} className="text-center">
                        <div className={`rounded-md py-2 text-[11px] font-semibold ${cell[row.m[i]]}`}>{row.m[i]}</div>
                        <div className="text-[10px] text-slate-400 mt-1">{m}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-4 mt-3 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-[#f5a524]" /> {t("speciesList.peak")}</span>
                    <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-[#0e9488]" /> {t("speciesList.good")}</span>
                    <span className="flex items-center gap-1"><span className="h-3 w-3 rounded bg-white/10" /> {t("speciesList.off")}</span>
                  </div>
                </div>
              )}
            </Reveal>
          </div>
          <div>
            <div className="sticky top-24 rounded-2xl border border-white/10 bg-[#0e2740] p-6 space-y-4">
              <div className="flex items-center gap-3"><CalendarDays className="h-5 w-5 text-[#f5a524]" /><div><p className="text-xs text-slate-400">{t("species.bestSeason")}</p><p className="text-white font-heading">{s.peak}</p></div></div>
              <div className="flex items-center gap-3"><Ruler className="h-5 w-5 text-[#f5a524]" /><div><p className="text-xs text-slate-400">{t("speciesList.avgWeight")}</p><p className="text-white font-heading">{s.weight}</p></div></div>
              <div className="flex items-center gap-3"><Target className="h-5 w-5 text-[#f5a524]" /><div><p className="text-xs text-slate-400">{t("speciesList.method")}</p><p className="text-white font-heading">{s.method}</p></div></div>
              <div className="flex items-center gap-3"><MapPin className="h-5 w-5 text-[#f5a524]" /><div><p className="text-xs text-slate-400">{t("speciesList.zone")}</p><p className="text-white font-heading capitalize">{zoneLabel}</p></div></div>
              <Button className="btn-gold w-full gap-2" onClick={() => openDeposit()}><Anchor className="h-4 w-4" /> {t("speciesList.bookCharter")}</Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-6">
        <div className="container-x">
          <h3 className="font-heading text-2xl text-white mb-5">{t("speciesList.moreSpecies", { zone: zoneLabel })}</h3>
          <div className="grid gap-4 sm:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to={`/species/${r.slug}`} className="card-hover group rounded-xl overflow-hidden relative h-48 border border-white/10 block">
                <img src={r.image} alt={r.name} className="absolute inset-0 h-full w-full object-cover object-center transition group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f33] to-transparent" />
                <span className="absolute bottom-3 left-3 font-heading text-xl text-white">{r.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <CTASection image={s.image} />
    </>
  );
};

export default SpeciesDetail;
